import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box, Title, Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import parseMarkdownLinks from '../../lib/parse-md-links';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import styles from './jump-link-nav.module.less';

const navigationItemMaxWidth = 154;

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};

const NavigationItemImage = ({ imageUrl, imageRolloverUrl, alt, url }) => {
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  if (!imageUrl) {
    return null;
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link href={url}>
        <Image
          naturalHeight={73}
          naturalWidth={navigationItemMaxWidth}
          backgroundColor="transparent"
          src={isHovered && imageRolloverUrl ? imageRolloverUrl : imageUrl}
          alt={alt}
        />
      </Link>
    </div>
  );
};

const JumpLinkNav = ({
  bgOptions,
  title,
  titleSize,
  titleColorOptions,
  subtitle,
  subtitleColorOptions,
  subtitleSize,
  titleChronicle,
  navigationItems,
  dangerouslyAllowHTMLInSubtitle,
}) => {
  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => {
        return (
          <div className={styles.container} style={{ backgroundColor }}>
            <Title
              size={titleSize}
              style={getTextColorStyle(titleColorOptions)}
              className={classNames([
                styles.title,
                {
                  [styles.inChronicle]: titleChronicle,
                },
              ])}
              id="title"
            >
              {title}
            </Title>

            <Box
              className={styles.subtitleBox}
              style={getTextColorStyle(subtitleColorOptions)}
            >
              <Text size={subtitleSize} id="subtitle">
                {htmlOutputParser(subtitle, dangerouslyAllowHTMLInSubtitle)}
              </Text>
            </Box>
            <Box className={styles.navigationItemsBox}>
              {navigationItems.map((navigationItem) => (
                <Box
                  key={navigationItem.$unique_key}
                  style={{
                    maxWidth:
                      navigationItem.imageMaxWidth || navigationItemMaxWidth,
                  }}
                >
                  <NavigationItemImage
                    imageUrl={navigationItem.imageUrl}
                    imageRolloverUrl={navigationItem.imageRolloverUrl}
                    alt={navigationItem.alt || 'Navigation Image'}
                    url={navigationItem.url}
                  />
                </Box>
              ))}
            </Box>
          </div>
        );
      }}
    </BackgroundColor>
  );
};

JumpLinkNav.displayName = 'JumpLinkNav';

NavigationItemImage.propTypes = {
  imageUrl: PropTypes.string,
  imageRolloverUrl: PropTypes.string,
  alt: PropTypes.string,
  url: PropTypes.string,
  children: PropTypes.node,
};

NavigationItemImage.defaultProps = {
  children: null,
};

JumpLinkNav.propTypes = {
  ...BackgroundColor.propTypes,
  title: PropTypes.string,
  titleSize: PropTypes.oneOf(['default', 'small']),
  subtitle: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['default', 'large', 'small', 'x-small']),
  navigationItems: PropTypes.array,
  dangerouslyAllowHTMLInSubtitle: PropTypes.bool,
  children: PropTypes.node,
};

JumpLinkNav.defaultProps = {
  ...BackgroundColor.defaultProps.bgOptions,
  title: '',
  titleSize: 'default',
  subtitle: '',
  subtitleSize: 'default',
  dangerouslyAllowHTMLInSubtitle: false,
  navigationItems: [],
  children: null,
};

JumpLinkNav.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  stringInputSpec('title', 'Title'),
  stringSelectSpec(
    `titleSize`,
    'Title Size',
    [
      { label: 'Default', value: 'default' },
      { label: 'Small', value: 'small' },
    ],
    {
      help: 'Defaults to default',
    }
  ),
  boolCheckboxSpec('titleChronicle', 'Title in Chronicle?'),
  ...getTextColor.getEditingConfig(
    componentDocument,
    'titleColorOptions',
    'Title Color'
  ),
  stringInputSpec('subtitle', 'Subtitle'),
  stringSelectSpec(
    `subtitleSize`,
    'Subtitle Size',
    [
      { label: 'Default', value: 'default' },
      { label: 'Large', value: 'large' },
      { label: 'Small', value: 'small' },
      { label: 'X-Small', value: 'x-small' },
    ],
    {
      help: 'Defaults to default',
    }
  ),
  ...getTextColor.getEditingConfig(
    componentDocument,
    'subtitleColorOptions',
    'Subtitle Color'
  ),
  boolCheckboxSpec(
    'dangerouslyAllowHTMLInSubtitle',
    'Allow HTML in Subtitle?',
    {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }
  ),
  repeatable('navigationItems', 'Navigation Items', 'Navigation Item', () => [
    stringInputSpec('altText', 'Alternate Image text'),
    stringInputSpec('imageUrl', 'Image URL'),
    stringInputSpec('imageRolloverUrl', 'Image Rollover URL'),
    stringInputSpec('url', 'URL'),
    stringInputSpec('imageMaxWidth', 'Image Max Width', {
      help: 'Default to 154px',
    }),
  ]),
];

export default JumpLinkNav;

<JumpLinkNav />;
